import React, { useEffect, useState, useCallback, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { checkoutCountryList as countryList } from 'shared/data/siteSettings';
import { Address as AccountAddress } from 'shared/types/account';
import { ShippingMethod } from 'shared/types/cart';
import Loading from 'components/commercetools-ui/atoms/loading';
import Overlay from 'components/commercetools-ui/atoms/overlay';
import CollectInStore from 'components/commercetools-ui/organisms/checkout/components/collect-in-store';
import AddressesPreview from 'components/commercetools-ui/organisms/checkout/components/steps/previews/addresses';
import useMappers from 'components/commercetools-ui/organisms/checkout/components/steps/sections/addresses/hooks/useMappers';
import { Address } from 'components/commercetools-ui/organisms/checkout/components/steps/sections/addresses/types';
import { validateCartForRestrictedItems } from 'components/commercetools-ui/organisms/checkout/components/steps/sections/payment/helper/apply-pay-helper';
import Shipping from 'components/commercetools-ui/organisms/checkout/components/steps/sections/shipping';
import { DataLayerHelpers, DataLayerOnLoad } from 'helpers/dataLayerHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import { useCart, useAccount } from 'frontastic';
import { CartDetails } from 'frontastic/hooks/useCart/types';
import { AmazonReviewData } from '../../types/AmazonReviewData';
import { amazonSessionAddress } from '../../types/amazonSessionAddress';
import AmazonpayAddressesPreview from '../amazonpay-address';
import AmazonPaymentPreview from '../amazonpay-payment';
import AmazonpayReviewShippingSkeleton from '../amazonpay-review-shipping-skeleton';
import AmazonPayShippingPreview from '../amazonpay-shipping-preview';

const initialAddressData = {
  addressId: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  line1: '',
  line2: '',
  postalCode: '',
  city: '',
  county: '',
  country: '',
} as Address;

interface AmazonReviewProps {
  amazonCheckoutSessionId: string;
  amazonCheckoutSessionData: AmazonReviewData;
  paymentError?: {
    result: boolean;
    message: string;
  };
  enablePurchaseButton: (value: boolean) => void;
}

const AmazonReviewComponent: React.FC<AmazonReviewProps> = ({
  amazonCheckoutSessionId,
  amazonCheckoutSessionData,
  enablePurchaseButton,
  paymentError = {
    result: false,
    message: '',
  },
}) => {
  const { getAvailableShippingMethods, setDeliveryOption, updateCart, setShippingMethod, data } = useCart();
  const { getCountry, locale, getCurrency } = useLocale();
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [collectInStoreOverlayOpen, setCollectInStoreOverlayOpen] = useState(false);
  const [showAvailableShippingOptions, setShowAvailableShippingOptions] = useState(false);
  const [shippingCountrySupported, setShippingCountrySupported] = useState(true);
  const [showShippingSection, setShowShippingSection] = useState(false);
  const [isDeliveryError, setIsDeliveryError] = useState(false);
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });
  const { account, loggedIn } = useAccount();
  const { addressToAccountAddress } = useMappers();
  const [sameShippingAddress, setSameShippingAddress] = useState(true);
  const [isCartAddressUpdated, setisCartAddressUpdated] = useState(false);
  const [editOptionEnabled, setEditOptionEnabled] = useState(false);
  const [active, setActive] = useState(0);
  const editCTAClass =
    'text-14 text-secondary-black underline decoration-secondary-black underline-offset-2 hover:cursor-pointer';
  const sectionMainClass =
    'flex items-center justify-between rounded-t-sm border border-neutral-400 bg-white p-12 transition lg:rounded-t-md lg:bg-white lg:px-36 lg:py-24';
  const sectionMainClassActive = sectionMainClass + ' lg:border-none';

  const isCountryCodePresent = (countryCode: string) => {
    return countryList.some((country) => country.countryCode === countryCode);
  };

  const amazaonAddressToAccountAddress = useCallback(
    (address: amazonSessionAddress) => {
      return {
        addressId: '',
        firstName: address.firstName ? address.firstName.split(' ')[0] : '',
        lastName: address.firstName ? address.firstName.split(' ')[1] : '',
        streetName: address.streetNumber ?? '',
        additionalAddressInfo: address.streetName ?? '',
        postalCode: address.postalCode ?? '',
        city: address.city ?? '',
        country: address.country || getCountry(),
        phone: '',
        state: '',
        county: address.county ? address.county : '',
      } as AccountAddress;
    },
    [account],
  );

  const isShippingMethodIdPresent = (productShippingMethods: ShippingMethod[]) => {
    const activeShippingMethodId = data?.shippingInfo?.shippingMethodId || '';
    if (activeShippingMethodId == '') {
      return false;
    }
    return productShippingMethods.some((method) => method.shippingMethodId === activeShippingMethodId);
  };

  useEffect(() => {
    const localeData = {
      country: getCountry(),
      currency: getCurrency(),
    };
    const currentUrl = document.location.origin;

    const dataLayer = new DataLayerOnLoad(currentUrl, 'checkout', localeData, account);
    dataLayer.setDataLayerOnLoad();
  }, []);
  useEffect(() => {
    const updateCartAddress = async () => {
      enablePurchaseButton(false);
      let hasDeliveryError = false;
      try {
        let openCollectInStoreOverlay = false;
        if (amazonCheckoutSessionData?.productType == 'PayAndShip') {
          const checkoutData = {
            shipping: amazaonAddressToAccountAddress(amazonCheckoutSessionData?.shippingAddress),
            billing: amazaonAddressToAccountAddress(amazonCheckoutSessionData?.billingAddress),
            account: {
              email: amazonCheckoutSessionData?.email,
            },
          } as CartDetails;
          setDeliveryOption('HomeDelivery');

          setShowShippingSection(false);
          setShowAvailableShippingOptions(false);

          const res = await updateCart(checkoutData);

          const isItemRestricted = validateCartForRestrictedItems(res, amazonCheckoutSessionData?.shippingAddress);

          const productShippingMethods = await getAvailableShippingMethods();

          if (productShippingMethods.length === 0 || isItemRestricted) {
            hasDeliveryError = true;
            setIsDeliveryError(true);
            setShowShippingSection(true);
            setShowAvailableShippingOptions(true);
          }

          if (productShippingMethods.length > 0 && !isItemRestricted) {
            //Check if ShippingMethodId is already set
            if (!isShippingMethodIdPresent(productShippingMethods)) {
              setShowAvailableShippingOptions(true);
              await setShippingMethod(productShippingMethods[0].shippingMethodId, '', new Date());
              setShowShippingSection(true);
            } else {
              setShowAvailableShippingOptions(false);
              setShowShippingSection(true);
            }
            enablePurchaseButton(true);
          }
        }
        if (amazonCheckoutSessionData?.productType == 'PayOnly') {
          //Store Collection
          setShowShippingSection(false);
          let checkoutData = {
            shipping: addressToAccountAddress(initialAddressData),
            billing: amazaonAddressToAccountAddress(amazonCheckoutSessionData?.billingAddress),
            account: {
              email: amazonCheckoutSessionData?.email,
            },
          } as CartDetails;
          const isStoreCollection = data?.custom?.deliveryOption == 'StoreCollection' ? true : false;
          if (!isStoreCollection) {
            openCollectInStoreOverlay = true;
            await setDeliveryOption('StoreCollection');
          } else {
            checkoutData = {
              shipping: data?.shippingAddress,
              billing: amazaonAddressToAccountAddress(amazonCheckoutSessionData?.billingAddress),
              account: {
                email: amazonCheckoutSessionData?.email,
              },
            } as CartDetails;
          }
          const res = await updateCart(checkoutData);
        }
        const overlayOpen =
          amazonCheckoutSessionData?.productType == 'PayOnly' &&
          (openCollectInStoreOverlay || data?.shippingAddress?.postalCode === '');
        if (overlayOpen) {
          setCollectInStoreOverlayOpen(true);
          setActive(1);
        } else {
          if (!hasDeliveryError) {
            enablePurchaseButton(true);
          }
        }
        enableEditOptions();
      } catch (error) {
        console.error('Error updating the address:', error);
      }
    };
    if (!isCartAddressUpdated) {
      setisCartAddressUpdated(true);
      const shippingAddress = amazonCheckoutSessionData?.shippingAddress;
      const country = shippingAddress?.country || '';

      if (amazonCheckoutSessionData?.productType === 'PayAndShip' && !isCountryCodePresent(country)) {
        setShippingCountrySupported(false);
        setIsDeliveryError(true);
        setShowShippingSection(true);
        showShippingOptions();
        enableEditOptions();
        enablePurchaseButton(false);
      } else {
        updateCartAddress();
      }
    }
  }, []);

  const hasInitializedDataLayer = useRef(false);
  useEffect(() => {
    const currentUrl = document.location.origin;
    if (!hasInitializedDataLayer.current && data !== undefined) {
      const salutation = loggedIn ? account?.salutation || '' : '';
      const paymentMethod = 'AmazonPay';
      const dataLayer = new DataLayerHelpers(
        currentUrl,
        locale,
        data as Record<string, any>,
        account?.email,
        account?.accountId,
        salutation,
        paymentMethod,
      );
      const dataLayerData = {
        breadcrumb: 'Checkout / AmazonPay Summmary',
        event: 'checkout',
        step: 4,
        option: paymentMethod,
        store: data?.shippingAddress?.streetName || '',
      };

      dataLayer.setCartDataLayer(dataLayerData);
      hasInitializedDataLayer.current = true;
    }
  }, [data]);

  const updateStoreAddress = async (storeShippingAddress: Address) => {
    setLoadingAddress(true);
    setCollectInStoreOverlayOpen(false);
    setActive(0);
    try {
      if (amazonCheckoutSessionData?.productType == 'PayOnly') {
        const checkoutData = {
          shipping: addressToAccountAddress(storeShippingAddress),
          billing: amazaonAddressToAccountAddress(amazonCheckoutSessionData?.billingAddress),
        } as CartDetails;
        const res = await updateCart(checkoutData);
        enablePurchaseButton(true);
      }
      setLoadingAddress(false);
    } catch (error) {
      console.error('Error in updating the store address:', error);
      setLoadingAddress(false);
    }
  };

  const setSelectedStoreAddress = (storeAddress: Address) => {
    updateStoreAddress(storeAddress);
  };

  const enableEditOptions = () => {
    const script = document.createElement('script');
    script.src = 'https://static-eu.payments-amazon.com/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      // bind Change Action
      (window as any).amazon.Pay.bindChangeAction('#editPaymentCTA', {
        amazonCheckoutSessionId: amazonCheckoutSessionId,
        changeAction: 'changePayment',
      });
      if (amazonCheckoutSessionData?.productType == 'PayAndShip') {
        (window as any).amazon.Pay.bindChangeAction('#editAddressCTA', {
          amazonCheckoutSessionId: amazonCheckoutSessionId,
          changeAction: 'changeAddress',
        });
      }
      setEditOptionEnabled(true);
    };

    // Clean up the script when the component is unmounted
    return () => {
      const scriptElement = document.querySelector('script[src="https://static-eu.payments-amazon.com/checkout.js"]');
      if (scriptElement) {
        scriptElement.remove();
      }
    };
  };

  const showCollectInStore = () => {
    setCollectInStoreOverlayOpen(true);
    setActive(1);
  };
  const goToNextStep = useCallback(() => {
    setShowAvailableShippingOptions(false);
  }, []);

  const showShippingOptions = () => {
    setShowAvailableShippingOptions(true);
  };

  return (
    <>
      <div className="px-16 md:px-24 lg:w-[70%] lg:grow lg:px-0">
        <div className="flex flex-col gap-24 lg:bg-neutral-200">
          <div className="bg-white">
            {loadingAddress && (
              <>
                <Overlay />
                <div className="p-20">
                  <Loading />
                </div>
              </>
            )}
            {/* Show address details*/}
            {!loadingAddress && (
              <>
                <div className={sectionMainClass}>
                  <div className="flex cursor-default items-center gap-12 lg:gap-16">
                    <h5 className="text-primary-black transition lg:text-18 lg:text-primary-black">
                      {formatCheckoutMessage({ id: 'delivery', defaultMessage: 'Delivery' })}
                    </h5>
                  </div>
                  {amazonCheckoutSessionData?.productType == 'PayAndShip' && (
                    <span
                      className={editCTAClass + `${editOptionEnabled ? '' : 'pointer-events-none cursor-default'}`}
                      id="editAddressCTA"
                    >
                      {editOptionEnabled ? (
                        formatCheckoutMessage({ id: 'edit', defaultMessage: 'Edit' })
                      ) : (
                        <div className="relative mb-10 h-15 w-[30px]">
                          <Skeleton></Skeleton>
                        </div>
                      )}
                    </span>
                  )}
                  {amazonCheckoutSessionData?.productType == 'PayOnly' && (
                    <span
                      className={editCTAClass + `${editOptionEnabled ? '' : 'pointer-events-none cursor-default'}`}
                      onClick={showCollectInStore}
                    >
                      {editOptionEnabled ? (
                        formatCheckoutMessage({ id: 'edit', defaultMessage: 'Edit' })
                      ) : (
                        <div className="relative mb-10 h-15 w-[30px]">
                          <Skeleton></Skeleton>
                        </div>
                      )}
                    </span>
                  )}
                </div>
                {amazonCheckoutSessionData?.productType == 'PayAndShip' && (
                  <AmazonpayAddressesPreview amazonpayReviewData={amazonCheckoutSessionData} />
                )}
                {amazonCheckoutSessionData?.productType == 'PayOnly' && (
                  <>
                    <AddressesPreview showAddressMatchHelpText={false} />
                    <CollectInStore
                      isOpen={active === 1 && collectInStoreOverlayOpen}
                      setIsOpen={setCollectInStoreOverlayOpen}
                      active={active}
                      setSameShippingAddress={setSameShippingAddress}
                      setShippingAddress={setSelectedStoreAddress}
                    />
                  </>
                )}
              </>
            )}
            {/* */}
          </div>

          {showShippingSection && (
            <div className="bg-white">
              <div>
                <div className={showAvailableShippingOptions ? sectionMainClassActive : sectionMainClass}>
                  <div className="flex cursor-default items-center gap-12 lg:gap-16">
                    <h5 className="text-primary-black transition lg:text-18 lg:text-primary-black">
                      {formatCheckoutMessage({ id: 'shipping', defaultMessage: 'Shipping' })}
                    </h5>
                  </div>
                  {!showAvailableShippingOptions && (
                    <span
                      onClick={showShippingOptions}
                      className={
                        editCTAClass + `${editOptionEnabled ? '' : 'pointer-events-none cursor-default opacity-25'}`
                      }
                    >
                      {formatCheckoutMessage({ id: 'edit', defaultMessage: 'Edit' })}
                    </span>
                  )}
                </div>

                {!showAvailableShippingOptions && <AmazonPayShippingPreview />}
                <div className={showAvailableShippingOptions ? '' : 'hidden'}>
                  <Shipping
                    CTAText={formatCheckoutMessage({ id: 'select', defaultMessage: 'Select' })}
                    goToNextStep={goToNextStep}
                    isDeliveryError={isDeliveryError}
                  />
                </div>
              </div>
            </div>
          )}

          {!showShippingSection && amazonCheckoutSessionData?.productType == 'PayAndShip' && (
            <div className="bg-white">
              <AmazonpayReviewShippingSkeleton />
            </div>
          )}

          <div className="bg-white">
            <div>
              <div className={sectionMainClass}>
                <div className="flex cursor-default items-center gap-12 lg:gap-16">
                  <h5 className="text-primary-black transition lg:text-18 lg:text-primary-black">
                    {formatCheckoutMessage({ id: 'payment', defaultMessage: 'Payment' })}
                  </h5>
                </div>
                <span
                  className={editCTAClass + `${editOptionEnabled ? '' : 'pointer-events-none cursor-default'}`}
                  id="editPaymentCTA"
                >
                  {editOptionEnabled ? (
                    formatCheckoutMessage({ id: 'edit', defaultMessage: 'Edit' })
                  ) : (
                    <div className="relative mb-10 h-15 w-[30px]">
                      <Skeleton></Skeleton>
                    </div>
                  )}
                </span>
              </div>
              <AmazonPaymentPreview
                paymentError={paymentError}
                paymentDescription={amazonCheckoutSessionData?.paymentInformation.paymentDescriptor || ''}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AmazonReviewComponent;
